import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslations } from "next-intl";
import { joinClassNames } from "@utils/helpers";
import { useWindowSize } from "@utils/hooks";
import { Tooltip, Text, Heading, Card, Button } from "@components/theming";
import { useIsShopsite } from "@context/DomainContext";
import DialogCloseButton from "@components/popovers/dialogCloseButton";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@components/popovers/dialog";

interface Props {
  children: React.ReactNode;
  showTooltip: boolean;
  onClose: () => void;
}

const StepTooltip = ({ children, showTooltip, onClose }: Props) => {
  const windowSize = useWindowSize();
  const [open, setOpen] = useState(false);
  const isShopsite = useIsShopsite();
  const t = useTranslations("Checkout.Stepper.nonnavigable");

  if (!showTooltip) {
    return <>{children}</>;
  }

  return windowSize.width && windowSize.width > 768 ? (
    <Tooltip
      sideOffset={10}
      side="bottom"
      content={
        <Text as="span" sharedClasses="text-sm">
          {t("content")}
        </Text>
      }
    >
      {children}
    </Tooltip>
  ) : (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) {
          onClose();
        }
      }}
    >
      <DialogTrigger>{children}</DialogTrigger>
      <AnimatePresence mode="wait">
        {open && (
          <DialogContent
            asChild
            forceMount
            handleClose={() => {
              setOpen(false);
              onClose();
            }}
          >
            <Card asChild>
              <motion.div
                initial="hidden"
                animate="show"
                exit="hidden"
                className="flex flex-col"
                transition={{ duration: 0.2 }}
                variants={{
                  hidden: {
                    y: 200,
                    opacity: 0,
                  },
                  show: {
                    y: 0,
                    opacity: 1,
                  },
                }}
              >
                <div
                  className={joinClassNames(
                    "flex flex-col gap-4",
                    isShopsite ? "px-6 pt-6 pb-4 bg-indigo-500" : ""
                  )}
                >
                  <div className="relative self-end">
                    <DialogClose asChild>
                      <DialogCloseButton shopsiteVariant="white" />
                    </DialogClose>
                  </div>
                  <DialogHeader className="!text-left">
                    <DialogTitle asChild>
                      <Heading
                        as="h3"
                        size="5"
                        weight="bold"
                        shopsiteClasses="text-white text-xl font-bold"
                      >
                        {t("title")}
                      </Heading>
                    </DialogTitle>
                  </DialogHeader>
                </div>
                <div className={isShopsite ? "px-6" : ""}>
                  <Text>{t("content")}</Text>
                </div>
                <div
                  className={joinClassNames(
                    "flex-1 flex items-center justify-center",
                    isShopsite ? "px-6 pb-6" : "pb-3"
                  )}
                >
                  <DialogClose asChild>
                    <Button sharedClasses="w-full">{t("done")}</Button>
                  </DialogClose>
                </div>
              </motion.div>
            </Card>
          </DialogContent>
        )}
      </AnimatePresence>
    </Dialog>
  );
};

export default StepTooltip;
